import { Assessment, Straighten } from '@mui/icons-material';
import * as React from 'react';
import { IntlShape } from 'react-intl';
import { DoseReportType } from '../../../api/dto';
import { getFacilities } from '../../../api/values';
import { fetchCernEmployeesByName } from '../../../lib/api';
import { TFormElement } from '../../types';
import { UiSchemaType } from '../../UiSchemaType';
import { activitySchema } from '../activity';
import { dimrSchema } from '../dimr';
import { orgUnitSchema } from '../orgUnit';
import { personSchema } from '../person';
import { supplierSchema } from '../supplier';
import { valueSchema } from '../value';

export const DOSE_REPORT_TYPES = [
    DoseReportType.SUMMARY,
    DoseReportType.RADIATION_DOSE_REPORT_BY_ACTIVITY,
    DoseReportType.DAILY_RADIATION_DOSE_REPORT_BY_ACTIVITY,
    DoseReportType.RADIATION_DOSE_REPORT_BY_PERSON,
    DoseReportType.HISTORICAL_RADIATION_DOSE,
] as const;

const doseReportTypeLabels = (intl: IntlShape): Record<DoseReportType, string> => ({
    [DoseReportType.SUMMARY]: intl.formatMessage({ id: 'doseReport.type.summary' }),
    [DoseReportType.RADIATION_DOSE_REPORT_BY_ACTIVITY]: intl.formatMessage({
        id: 'doseReport.type.radiationDoseByActivity',
    }),
    [DoseReportType.DAILY_RADIATION_DOSE_REPORT_BY_ACTIVITY]: intl.formatMessage({
        id: 'doseReport.type.dailyRadiationDoseByActivity',
    }),
    [DoseReportType.RADIATION_DOSE_REPORT_BY_PERSON]: intl.formatMessage({
        id: 'doseReport.type.radiationDoseByPerson',
    }),
    [DoseReportType.HISTORICAL_RADIATION_DOSE]: intl.formatMessage({
        id: 'doseReport.type.historicalRadiationDose',
    }),
});

export const doseReportFormSchema = (intl: IntlShape, showThresholds: boolean): TFormElement => {
    const labels = doseReportTypeLabels(intl);

    const parameters: TFormElement = {
        type: UiSchemaType.LAYOUT_GRID,
        elements: [
            {
                xs: 12,
                element: {
                    type: UiSchemaType.AUTOCOMPLETE,
                    label: intl.formatMessage({ id: 'doseReport.field.report' }),
                    path: 'type',
                    required: true,
                    typeProperties: {
                        options: [...DOSE_REPORT_TYPES],
                        matchId: false,
                        getOptionLabel: (option: any) => labels[option as DoseReportType] ?? option,
                        autocompleteProps: {
                            isOptionEqualToValue: (option: DoseReportType, value: DoseReportType) => option === value,
                        },
                    },
                },
            },
            {
                xs: 6,
                sm: 4,
                element: personSchema({
                    label: intl.formatMessage({ id: 'activity.field.participant' }),
                    path: 'participant',
                }),
            },
            {
                xs: 6,
                sm: 4,
                element: orgUnitSchema({
                    label: intl.formatMessage({ id: 'doseReport.field.participantUnit' }),
                    path: 'participantUnit',
                }),
            },
            {
                xs: 6,
                sm: 4,
                element: orgUnitSchema({
                    label: intl.formatMessage({ id: 'doseReport.field.activityResponsibleUnit' }),
                    path: 'activityResponsibleUnit',
                }),
            },
            {
                xs: 6,
                sm: 4,
                element: activitySchema(
                    {
                        label: intl.formatMessage({ id: 'activity.name' }),
                        path: 'activity',
                    },
                    intl
                ),
            },
            {
                xs: 6,
                sm: 4,
                element: personSchema({
                    label: intl.formatMessage({ id: 'document.field.activityResponsible' }),
                    path: 'activityResponsible',
                    fetchEndpoint: fetchCernEmployeesByName,
                }),
            },
            {
                xs: 6,
                sm: 4,
                element: supplierSchema({
                    label: intl.formatMessage({ id: 'doseReport.field.supplier' }),
                    path: 'supplier',
                }),
            },
            {
                xs: 6,
                element: dimrSchema(
                    {
                        label: intl.formatMessage({ id: 'dimr.name' }),
                        path: 'dimr',
                    },
                    intl
                ),
            },
            {
                xs: 6,
                element: valueSchema(
                    {
                        label: intl.formatMessage({ id: 'document.field.facility' }),
                        path: 'facility',
                        fetchOptions: getFacilities,
                        multiple: false,
                    },
                    intl.locale
                ),
            },
            {
                xs: 6,
                element: {
                    type: UiSchemaType.DATE,
                    label: intl.formatMessage({ id: 'document.field.from' }),
                    path: 'startDate',
                },
            },
            {
                xs: 6,
                element: {
                    type: UiSchemaType.DATE,
                    label: intl.formatMessage({ id: 'document.field.to' }),
                    path: 'endDate',
                },
            },
        ],
    };

    const thresholds: TFormElement = {
        type: UiSchemaType.LAYOUT_GRID,
        elements: [
            {
                xs: 12,
                sm: 6,
                md: 4,
                element: {
                    type: UiSchemaType.RANGE,
                    label: intl.formatMessage({ id: 'doseReport.field.collectiveDosePercentage' }),
                    typeProperties: {
                        fromPath: 'fromCollectivePercentageTaken',
                        toPath: 'toCollectivePercentageTaken',
                    },
                },
            },
            {
                xs: 12,
                sm: 6,
                md: 4,
                element: {
                    type: UiSchemaType.RANGE,
                    label: intl.formatMessage({ id: 'doseReport.field.individualDosePercentage' }),
                    typeProperties: {
                        fromPath: 'fromPercentageTaken',
                        toPath: 'toPercentageTaken',
                    },
                },
            },
            {
                xs: 12,
                sm: 6,
                md: 4,
                element: {
                    type: UiSchemaType.RANGE,
                    label: intl.formatMessage({ id: 'doseReport.field.lastTwelveMonths' }),
                    typeProperties: {
                        fromPath: 'fromIndividualHistoDose',
                        toPath: 'toIndividualHistoDose',
                    },
                },
            },
        ],
    };

    return {
        type: UiSchemaType.LAYOUT_VERTICAL,
        elements: [
            {
                type: UiSchemaType.PANEL,
                label: intl.formatMessage({ id: 'doseReport.panel.parameters' }),
                typeProperties: {
                    icon: <Assessment color="action" />,
                },
                element: parameters,
            },
            ...(showThresholds
                ? [
                      {
                          type: UiSchemaType.PANEL,
                          label: intl.formatMessage({ id: 'doseReport.panel.thresholds' }),
                          typeProperties: {
                              icon: <Straighten color="action" />,
                          },
                          element: thresholds,
                      } satisfies TFormElement,
                  ]
                : []),
        ],
    };
};
